import useSWR from "swr";
import { fetcher } from "@/lib/api";
import KpiTile from "@/components/KpiTile";
import { PageHeader, Section } from "@/components/Heading";
import { formatIndianCurrency, formatIndianGrouping } from "@/lib/format";
import { Sun, TrendingUp } from "lucide-react";
import { usePeriod } from "@/components/PeriodFilterBar";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const PERIOD_LABEL = { DAY: "Today", WEEK: "Last 7 days", MTD: "Month to date",
                       QTD: "Quarter to date", YTD: "Year to date (FY)", LTM: "Last 12 months" };
const DELTA_LABEL = { DAY: "vs yest", WEEK: "vs prev 7d", MTD: "vs prev mo",
                      QTD: "vs prev Q", YTD: "vs prev FY", LTM: "vs prev 12m" };

export default function DailyBrief() {
  const { period, dept } = usePeriod();
  const q = `period=${period}${dept && dept !== "ALL" ? `&dept=${encodeURIComponent(dept)}` : ""}`;

  const { data: kpi } = useSWR(`/v2/kpis?${q}`, fetcher, { refreshInterval: 30000 });
  const { data: batch } = useSWR(`/v2/batch?${q}`, fetcher);
  const { data: insight } = useSWR("/ai/insight-of-day", fetcher);
  const { data: alerts } = useSWR("/stock/critical-alerts", fetcher);

  const isEmpty = kpi?.is_empty_today;
  const deltaLabel = DELTA_LABEL[period] || "vs prev";
  const hourly = (batch?.hourly || []).filter((h) => h.value > 0);

  return (
    <div>
      <PageHeader
        eyebrow={`Daily Brief · ${PERIOD_LABEL[period] || period}${dept && dept !== "ALL" ? ` · ${dept}` : ""}`}
        title="Today's read of the floor."
        lede="The five numbers your category heads should be on top of before the doors open."
      />

      {isEmpty && (
        <div data-testid="empty-state" className="hairline px-6 py-6 mb-10 flex items-start gap-4 bg-secondary/30">
          <Sun className="w-5 h-5 mt-0.5 text-warning" />
          <div>
            <div className="font-display text-xl">Stores haven't billed yet today.</div>
            <div className="text-sm text-muted-foreground mt-1">
              First transaction usually rings around 10:30 AM. Live tickers update every 30 seconds — check Pulse Feed.
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border">
        <KpiTile testId="kpi-net-sales-tile" label={`Net Sales · ${PERIOD_LABEL[period] || period}`} value={kpi?.net_sales || 0}
          deltas={[{ value: kpi?.vs_yesterday_pct, label: deltaLabel }, { value: kpi?.vs_target_pct, label: "vs tgt" }]} accent />
        <KpiTile testId="kpi-transactions-tile" label="Transactions" kind="number" value={kpi?.transactions || 0} />
        <KpiTile testId="kpi-atv-tile" label="Avg Transaction" value={kpi?.atv || 0} />
        <KpiTile testId="kpi-units-tile" label="Units Sold" kind="number" value={kpi?.units_sold || 0} sub={`${kpi?.margin_pct || 0}% margin`} />
      </div>

      <Section eyebrow="Editor's note" title="Insight of the day" testId="section-insight">
        <div className="hairline px-6 py-6">
          <p className="text-base md:text-lg leading-relaxed text-foreground" data-testid="ai-insight-text">
            {(insight?.text || "Drafting today's read…")
              .replace(/\*+/g, "")
              .replace(/^["']|["']$/g, "")
              .replace(/^\s*Insight of the Day[:\-–—]\s*/i, "")
              .replace(/^\s*Insight[:\-–—]\s*/i, "")
              .trim()}
          </p>
          <div className="label-caps mt-4">Generated by Claude Sonnet 4.5 · refreshes every 6h</div>
        </div>
      </Section>

      {/* Hourly + Departments split */}
      <div className={`grid gap-10 mt-14 ${hourly.length > 0 ? "lg:grid-cols-2" : "lg:grid-cols-1"}`}>
        {hourly.length > 0 && (
          <Section eyebrow="Cadence" title="Sales by hour · today">
            <div className="hairline p-4">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={hourly}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" />
                  <XAxis dataKey="hour" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} tickFormatter={(v) => formatIndianCurrency(v)} />
                  <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", fontSize: 12 }} formatter={(v) => formatIndianCurrency(v)} />
                  <Bar dataKey="value" fill="hsl(var(--primary))" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Section>
        )}
        <Section eyebrow="Departments" title={`Top performers · ${PERIOD_LABEL[period] || period}`}>
          <div className={`grid gap-x-10 gap-y-0 ${hourly.length > 0 ? "grid-cols-1" : "md:grid-cols-2"}`}>
            {(batch?.top_departments || []).slice(0, hourly.length > 0 ? 6 : 8).map((d, i) => (
              <div key={d.department} className="flex items-baseline gap-3 py-2.5 hairline-b">
                <span className="font-mono text-xs text-muted-foreground w-6">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-display text-base flex-1 truncate">{d.department}</span>
                <span className="font-mono text-sm">{formatIndianCurrency(d.net_sales)}</span>
              </div>
            ))}
            {(batch?.top_departments || []).length === 0 && (
              <div className="text-sm text-muted-foreground py-4">No sales in window.</div>
            )}
          </div>
        </Section>
      </div>

      {/* Stores + top products */}
      <div className="grid lg:grid-cols-2 gap-10 mt-14">
        <Section eyebrow="Stores" title="Bar by bar">
          {(batch?.top_stores || []).slice(0, 8).map((s) => {
            const max = (batch?.top_stores?.[0]?.net_sales) || 1;
            const pct = Math.max(2, (s.net_sales / max) * 100);
            return (
              <div key={s.store} className="py-2">
                <div className="flex items-baseline justify-between text-xs mb-1">
                  <span className="font-sans">{s.store}</span>
                  <span className="font-mono">{formatIndianCurrency(s.net_sales)}</span>
                </div>
                <div className="h-1.5 bg-secondary">
                  <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </Section>
        <Section eyebrow="Top fast-movers" title="Best-selling SKUs">
          <div className="space-y-1">
            {(batch?.top_products || []).slice(0, 8).map((p, i) => (
              <div key={p.name} className="flex items-baseline gap-3 py-2 hairline-b">
                <TrendingUp className="w-3 h-3 text-positive shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-display text-sm truncate">{p.name}</div>
                  <div className="text-[10px] text-muted-foreground">{p.department}</div>
                </div>
                <span className="font-mono text-sm">{formatIndianCurrency(p.sales_value)}</span>
              </div>
            ))}
          </div>
        </Section>
      </div>

      <Section eyebrow="Stock alerts" title="What runs out next" testId="section-critical-alerts">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
          {(alerts?.alerts || []).slice(0, 6).map((a) => (
            <div key={a.name} className="bg-background p-5">
              <div className="label-caps text-warning">Critical</div>
              <div className="font-display text-base mt-1 leading-tight">{a.name}</div>
              <div className="text-[11px] text-muted-foreground mt-1">{a.department} · {a.vendor_name}</div>
              <div className="grid grid-cols-3 mt-4 gap-2">
                <div><div className="label-caps">Qty</div><div className="font-mono text-sm mt-1">{formatIndianGrouping(a.closing_qty)}</div></div>
                <div><div className="label-caps">Rate/day</div><div className="font-mono text-sm mt-1">{a.daily_sales_rate}</div></div>
                <div><div className="label-caps">Days left</div><div className="font-mono text-sm mt-1 text-destructive">{a.days_left || "—"}</div></div>
              </div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
